import {Component, OnInit, ViewChild, ElementRef} from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { UserService } from 'projects/core/src/lib/login/data/user.service';
import { TimeCardService } from 'projects/app-misc/src/app/modules/punch-time-widget/data/time-card.service';
import { MiscSnackBarService } from 'projects/core/src/lib/misc/view/snackbar/misc-snackbar.service';
import { User } from 'projects/core/src/lib/login/data/user';
import { HttpErrorResponse } from '@angular/common/http';
import moment from 'moment';

interface FileStatus {
  file: File;
  status: 'pending' | 'uploading' | 'success' | 'error';
  error?: string;
}

@Component({
  selector: 'app-csv-import-export-dialog',
  templateUrl: './csv-import-export-dialog.component.html',
  styleUrls: ['./csv-import-export-dialog.component.scss']
})
export class CsvImportExportDialogComponent implements OnInit {
  users: User[] = [];
  selectedUsers: User[] = [];
  fileStatuses: FileStatus[] = [];
  isDragging = false;
  startDate: Date = moment().startOf('year').toDate();
  endDate: Date = moment().endOf('year').toDate();

  @ViewChild('fileInput') fileInput: ElementRef<HTMLInputElement>;

  constructor(
    protected dialogRef: MatDialogRef<CsvImportExportDialogComponent>,
    protected userService: UserService,
    protected timeCardService: TimeCardService,
    protected miscSnackBarService: MiscSnackBarService
  ) {
  }

  ngOnInit(): void {
    this.userService.getTrackUsers().subscribe(users => {
      this.users = users;
      this.selectedUsers = [];
    }, error => {
      this.miscSnackBarService.openErrorSnackBar('Error loading users');
      this.dialogRef.close();
    });
  }

  exportCsv() {
    if (this.selectedUsers.length === 0 || !this.startDate || !this.endDate) {
      return;
    }

    const userIds = this.selectedUsers.map(u => u.id).join(',');
    const dateFrom = moment(this.startDate).format('YYYY-MM-DD');
    const dateTo = moment(this.endDate).format('YYYY-MM-DD');
    this.timeCardService.exportTimesheetToCsv(userIds, dateFrom, dateTo);
  }


  triggerFileInput() {
    if (this.fileInput) {
      this.fileInput.nativeElement.click();
    }
  }

  onDragOver(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging = true;
  }

  onDragLeave(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging = false;
  }

  onDrop(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging = false;

    const files = event.dataTransfer?.files;
    if (files) {
      this.handleFiles(Array.from(files));
    }
  }

  onFileSelect(event: Event) {
    const input = event.target as HTMLInputElement;
    if (input.files) {
      this.handleFiles(Array.from(input.files));
    }
  }

  handleFiles(files: File[]) {
    const csvFiles = files.filter(f => f.name.endsWith('.csv'));

    if (csvFiles.length === 0) {
      this.miscSnackBarService.openErrorSnackBar('Please select CSV files');
      return;
    }

    csvFiles.forEach(file => {
      this.fileStatuses.push({
        file,
        status: 'pending'
      });
    });

    this.processNextFile();
  }

  processNextFile() {
    const pendingFile = this.fileStatuses.find(fs => fs.status === 'pending');

    if (!pendingFile) {
      return;
    }

    pendingFile.status = 'uploading';

    this.timeCardService.importTimesheetFromCsv(pendingFile.file).subscribe(
      () => {
        pendingFile.status = 'success';
        this.processNextFile();
      },
      (error: HttpErrorResponse) => {
        pendingFile.status = 'error';
        pendingFile.error = this.formatImportError(error);

        this.processNextFile();
      }
    );
  }

  private formatImportError(error: HttpErrorResponse): string {
    const raw = (error?.error?.message ?? error?.error ?? error?.message ?? 'Import failed');
    const text = typeof raw === 'string' ? raw : JSON.stringify(raw);
    return text.replace(/\\r\\n/g, '\n').replace(/\\n/g, '\n');
  }

  removeFile(fileStatus: FileStatus) {
    const index = this.fileStatuses.indexOf(fileStatus);
    if (index > -1) {
      this.fileStatuses.splice(index, 1);
    }
  }

  clearFiles() {
    this.fileStatuses = [];
  }

  disableClose() {
    this.dialogRef.disableClose = true;
    this.blurActiveElement();
  }

  enableClose() {
    this.dialogRef.disableClose = false;
  }

  blurActiveElement() {
    setTimeout(() => {
      const activeElement = document.activeElement;
      if (activeElement && activeElement instanceof HTMLElement)
        activeElement.blur();
    }, 5);
  }
}
